import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Grid,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Paper,
  Chip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { DataService } from 'src/services/data-service';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';

interface IdentificationType {
  id: string;
  name: string;
  active: boolean;
}

interface Tenant {
  id: string;
  fullName: string;
  phoneNumbers: string[];
  emails: string[];
  nationality: string | null;
  addressNumber?: string;
  addressStreet?: string;
  addressNeighborhood?: string;
  addressMunicipality?: string;
  addressCity?: string;
  identificationTypeId: string;
  username?: string;
}

interface TenantEditDialogProps {
  open: boolean;
  tenant: Tenant | null;
  onClose: () => void;
  onSuccess: () => void;
}

const TenantEditDialog: React.FC<TenantEditDialogProps> = ({
  open,
  tenant,
  onClose,
  onSuccess
}) => {
  const [formData, setFormData] = useState({
    fullName: '',
    phoneNumbers: [''],
    emails: [''],
    nationality: '',
    addressNumber: '',
    addressStreet: '',
    addressNeighborhood: '',
    addressMunicipality: '',
    addressCity: '',
    identificationTypeId: '',
    username: '',
    password: '',
    files: [] as File[]
  });
  const [identificationTypes, setIdentificationTypes] = useState<IdentificationType[]>([]);
  const [loadingIdentificationTypes, setLoadingIdentificationTypes] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const dispatch = useAppDispatch();

  // Fetch identification types when dialog opens
  useEffect(() => {
    if (open) {
      fetchIdentificationTypes();
    }
  }, [open]);

  const fetchIdentificationTypes = async () => {
    setLoadingIdentificationTypes(true);
    try {
      const response = await DataService.get('api/identification-types/all');
      if (response.ok) {
        const responseData = await response.json();
        let rawTypes: any[] = [];

        if (responseData.success && responseData.data && Array.isArray(responseData.data)) {
          rawTypes = responseData.data;
        } else if (Array.isArray(responseData)) {
          rawTypes = responseData;
        }

        const activeTypes = rawTypes
          .filter(type => type.active)
          .map(type => ({
            id: type.id,
            name: type.name,
            active: type.active
          }));

        console.log('Loaded identification types:', activeTypes);
        setIdentificationTypes(activeTypes);
      }
    } catch (error) {
      console.error('Error fetching identification types:', error);
      dispatch(showAlert({
        message: 'Failed to load identification types',
        type: 'error'
      }));
    } finally {
      setLoadingIdentificationTypes(false);
    }
  };

  // Initialize form data when tenant changes
  useEffect(() => {
    if (tenant && open) {
      console.log('Initializing edit form with tenant data:', tenant);
      setFormData({
        fullName: tenant.fullName || '',
        phoneNumbers: tenant.phoneNumbers.length > 0 ? tenant.phoneNumbers : [''],
        emails: tenant.emails.length > 0 ? tenant.emails : [''],
        nationality: tenant.nationality || '',
        addressNumber: tenant.addressNumber || '',
        addressStreet: tenant.addressStreet || '',
        addressNeighborhood: tenant.addressNeighborhood || '',
        addressMunicipality: tenant.addressMunicipality || '',
        addressCity: tenant.addressCity || '',
        identificationTypeId: tenant.identificationTypeId || '',
        username: tenant.username || '',
        password: '',
        files: []
      });
      setErrors({});
    }
  }, [tenant, open]);

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      setFormData({
        fullName: '',
        phoneNumbers: [''],
        emails: [''],
        nationality: '',
        addressNumber: '',
        addressStreet: '',
        addressNeighborhood: '',
        addressMunicipality: '',
        addressCity: '',
        identificationTypeId: '',
        username: '',
        password: '',
        files: []
      });
      setErrors({});
    }
  }, [open]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleArrayFieldChange = (field: 'phoneNumbers' | 'emails', index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => i === index ? value : item)
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const addArrayField = (field: 'phoneNumbers' | 'emails') => {
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field], '']
    }));
  };

  const removeArrayField = (field: 'phoneNumbers' | 'emails', index: number) => {
    if (formData[field].length > 1) {
      setFormData(prev => ({
        ...prev,
        [field]: prev[field].filter((_, i) => i !== index)
      }));
    }
  };

  // Handle file operations
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    setFormData(prev => ({
      ...prev,
      files: [...prev.files, ...files]
    }));
  };

  const removeFile = (index: number) => {
    setFormData(prev => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index)
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Validate full name
    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }

    // Validate emails (at least one valid email)
    const validEmails = formData.emails.filter(email => email.trim() !== '');
    if (validEmails.length === 0) {
      newErrors.emails = 'At least one email is required';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const hasInvalidEmail = validEmails.some(email => !emailRegex.test(email));
      if (hasInvalidEmail) {
        newErrors.emails = 'Please enter valid email addresses';
      }
    }

    // Validate phone numbers (at least one valid phone)
    const validPhones = formData.phoneNumbers.filter(phone => phone.trim() !== '');
    if (validPhones.length === 0) {
      newErrors.phoneNumbers = 'At least one phone number is required';
    }

    // Validate identification type
    if (!formData.identificationTypeId) {
      newErrors.identificationTypeId = 'Identification type is required';
    }

    // Username validation
    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    } else if (formData.username.trim().length < 3) {
      newErrors.username = 'Username must be at least 3 characters';
    }

    // Password validation (optional, but if provided must be valid)
    if (formData.password.trim() && formData.password.trim().length < 6) {
      newErrors.password = 'Password must be at least 6 characters if provided';
    }

    // Address validation - all or nothing
    const addressFields = [
      formData.addressNumber.trim(),
      formData.addressStreet.trim(),
      formData.addressNeighborhood.trim(),
      formData.addressMunicipality.trim(),
      formData.addressCity.trim()
    ];
    const filledAddressFields = addressFields.filter(field => field !== '');

    if (filledAddressFields.length > 0 && filledAddressFields.length < addressFields.length) {
      // At least one address field is filled but not all
      if (!formData.addressNumber.trim()) newErrors.addressNumber = 'Required when any address field is filled';
      if (!formData.addressStreet.trim()) newErrors.addressStreet = 'Required when any address field is filled';
      if (!formData.addressNeighborhood.trim()) newErrors.addressNeighborhood = 'Required when any address field is filled';
      if (!formData.addressMunicipality.trim()) newErrors.addressMunicipality = 'Required when any address field is filled';
      if (!formData.addressCity.trim()) newErrors.addressCity = 'Required when any address field is filled';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm() || !tenant) return;

    setSubmitting(true);

    try {
      // Create FormData for multipart/form-data (to support files)
      const submitFormData = new FormData();

      submitFormData.append('Id', tenant.id);
      submitFormData.append('FullName', formData.fullName.trim());
      submitFormData.append('Nationality', formData.nationality.trim() || '');
      submitFormData.append('IdentificationTypeId', formData.identificationTypeId);
      submitFormData.append('Username', formData.username.trim());

      // Only add password if it's been changed (not empty)
      if (formData.password.trim()) {
        submitFormData.append('Password', formData.password.trim());
      }

      // Add phone numbers
      const validPhones = formData.phoneNumbers.filter(phone => phone.trim() !== '');
      validPhones.forEach(phone => {
        submitFormData.append('PhoneNumbers', phone.trim());
      });

      // Add emails
      const validEmails = formData.emails.filter(email => email.trim() !== '');
      validEmails.forEach(email => {
        submitFormData.append('Emails', email.trim());
      });

      // Add address fields (optional)
      if (formData.addressNumber.trim()) {
        submitFormData.append('AddressNumber', formData.addressNumber.trim());
      }
      if (formData.addressStreet.trim()) {
        submitFormData.append('AddressStreet', formData.addressStreet.trim());
      }
      if (formData.addressNeighborhood.trim()) {
        submitFormData.append('AddressNeighborhood', formData.addressNeighborhood.trim());
      }
      if (formData.addressMunicipality.trim()) {
        submitFormData.append('AddressMunicipality', formData.addressMunicipality.trim());
      }
      if (formData.addressCity.trim()) {
        submitFormData.append('AddressCity', formData.addressCity.trim());
      }

      // Add files (optional)
      formData.files.forEach(file => {
        submitFormData.append('Files', file);
      });

      console.log('Updating tenant with data:');
      for (let [key, value] of submitFormData.entries()) {
        console.log(key, value);
      }

      const response = await DataService.postForm('api/Tenant/update', submitFormData);

      if (response.ok) {
        dispatch(showAlert({
          message: 'Tenant updated successfully!',
          type: 'success'
        }));
        onSuccess();
      } else {
        // Try to get error details from response
        let errorMessage = 'Failed to update tenant';
        try {
          const errorData = await response.json();
          console.error('API Error Response:', errorData);
          if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.errors) {
            const validationErrors = Object.values(errorData.errors).flat();
            errorMessage = validationErrors.join(', ');
          }
        } catch (e) {
          errorMessage = `Failed to update tenant: ${response.status} ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Error updating tenant:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to update tenant. Please try again.';
      dispatch(showAlert({
        message: errorMessage,
        type: 'error'
      }));
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      onClose();
    }
  };

  if (!tenant) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '60vh' }
      }}
    >
      <DialogTitle>
        <Typography variant="h5" component="div" sx={{ fontWeight: 'bold' }}>
          Edit Tenant
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Update tenant information for {tenant.fullName}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {loadingIdentificationTypes ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2 }}>
              Loading identification types...
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {/* Full Name */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Full Name"
                value={formData.fullName}
                onChange={(e) => handleInputChange('fullName', e.target.value)}
                error={!!errors.fullName}
                helperText={errors.fullName}
                disabled={submitting}
                required
              />
            </Grid>

            {/* Email Addresses */}
            <Grid item xs={12} md={6}>
              <Box>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium' }}>
                  Email Addresses *
                </Typography>
                {formData.emails.map((email, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <TextField
                      fullWidth
                      label={`Email ${index + 1}`}
                      value={email}
                      onChange={(e) => handleArrayFieldChange('emails', index, e.target.value)}
                      error={!!errors.emails}
                      disabled={submitting}
                      type="email"
                    />
                    {formData.emails.length > 1 && (
                      <IconButton
                        onClick={() => removeArrayField('emails', index)}
                        disabled={submitting}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Box>
                ))}
                <Button
                  startIcon={<AddIcon />}
                  onClick={() => addArrayField('emails')}
                  disabled={submitting}
                  size="small"
                >
                  Add Email
                </Button>
                {errors.emails && (
                  <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
                    {errors.emails}
                  </Typography>
                )}
              </Box>
            </Grid>

            {/* Phone Numbers */}
            <Grid item xs={12} md={6}>
              <Box>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium' }}>
                  Phone Numbers *
                </Typography>
                {formData.phoneNumbers.map((phone, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <TextField
                      fullWidth
                      label={`Phone ${index + 1}`}
                      value={phone}
                      onChange={(e) => handleArrayFieldChange('phoneNumbers', index, e.target.value)}
                      error={!!errors.phoneNumbers}
                      disabled={submitting}
                    />
                    {formData.phoneNumbers.length > 1 && (
                      <IconButton
                        onClick={() => removeArrayField('phoneNumbers', index)}
                        disabled={submitting}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Box>
                ))}
                <Button
                  startIcon={<AddIcon />}
                  onClick={() => addArrayField('phoneNumbers')}
                  disabled={submitting}
                  size="small"
                >
                  Add Phone
                </Button>
                {errors.phoneNumbers && (
                  <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
                    {errors.phoneNumbers}
                  </Typography>
                )}
              </Box>
            </Grid>

            {/* Nationality */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nationality"
                value={formData.nationality}
                onChange={(e) => handleInputChange('nationality', e.target.value)}
                disabled={submitting}
              />
            </Grid>

            {/* Identification Type */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors.identificationTypeId} required>
                <InputLabel>Identification Type</InputLabel>
                <Select
                  value={formData.identificationTypeId}
                  label="Identification Type"
                  onChange={(e) => handleInputChange('identificationTypeId', e.target.value)}
                  disabled={submitting}
                >
                  {identificationTypes.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.identificationTypeId && (
                  <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
                    {errors.identificationTypeId}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            {/* Username */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Username"
                value={formData.username}
                onChange={(e) => handleInputChange('username', e.target.value)}
                error={!!errors.username}
                helperText={errors.username}
                disabled={submitting}
                required
                placeholder="Enter username"
              />
            </Grid>

            {/* Password */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                error={!!errors.password}
                helperText={errors.password || 'Leave blank to keep current password'}
                disabled={submitting}
                placeholder="Enter new password (optional)"
              />
            </Grid>

            {/* Address Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2, fontWeight: 'medium' }}>
                Address Information (Optional)
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Address Number"
                value={formData.addressNumber}
                onChange={(e) => handleInputChange('addressNumber', e.target.value)}
                error={!!errors.addressNumber}
                helperText={errors.addressNumber}
                disabled={submitting}
                placeholder="Building/House number"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Street"
                value={formData.addressStreet}
                onChange={(e) => handleInputChange('addressStreet', e.target.value)}
                error={!!errors.addressStreet}
                helperText={errors.addressStreet}
                disabled={submitting}
                placeholder="Street name"
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Neighborhood"
                value={formData.addressNeighborhood}
                onChange={(e) => handleInputChange('addressNeighborhood', e.target.value)}
                error={!!errors.addressNeighborhood}
                helperText={errors.addressNeighborhood}
                disabled={submitting}
                placeholder="Neighborhood"
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Municipality"
                value={formData.addressMunicipality}
                onChange={(e) => handleInputChange('addressMunicipality', e.target.value)}
                error={!!errors.addressMunicipality}
                helperText={errors.addressMunicipality}
                disabled={submitting}
                placeholder="Municipality"
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="City"
                value={formData.addressCity}
                onChange={(e) => handleInputChange('addressCity', e.target.value)}
                error={!!errors.addressCity}
                helperText={errors.addressCity}
                disabled={submitting}
                placeholder="City"
              />
            </Grid>

            {/* File Upload */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2, fontWeight: 'medium' }}>
                Documents (Optional)
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                <input
                  accept="image/*,.pdf,.doc,.docx"
                  style={{ display: 'none' }}
                  id="file-upload-edit"
                  multiple
                  type="file"
                  onChange={handleFileChange}
                  disabled={submitting}
                />
                <label htmlFor="file-upload-edit">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<CloudUploadIcon />}
                    disabled={submitting}
                  >
                    Upload Files
                  </Button>
                </label>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Supported formats: Images, PDF, Word documents
                </Typography>
              </Paper>

              {formData.files.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Selected Files:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {formData.files.map((file, index) => (
                      <Chip
                        key={index}
                        icon={<AttachFileIcon />}
                        label={file.name}
                        onDelete={() => removeFile(index)}
                        disabled={submitting}
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Grid>
          </Grid>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button
          onClick={handleClose}
          disabled={submitting}
          variant="outlined"
          size="large"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={submitting || loadingIdentificationTypes}
          variant="contained"
          size="large"
          startIcon={submitting ? <CircularProgress size={16} /> : undefined}
        >
          {submitting ? 'Updating...' : 'Update Tenant'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TenantEditDialog;